import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSetSnackbarMessage } from '@tempo-io/fim3-components';
import type {
  CreateDemandTypeDto,
  CreateDemandTypeResponseDto,
  DemandTypeDto,
  GetDemandTypesResponseDto,
  UpdateDemandTypeDto,
  UpdateDemandTypeResponseDto,
} from '@tempo-io/fim3-dto';
import type { AxiosInstance } from 'axios';
import { useTranslation } from 'react-i18next';
import { demandTypesQueryKeys } from '@/lib/query-keys/demandTypes';
import { useErrorHandlingMutation } from './useErrorHandlingMutation';

export const useDemandTypesApi = (httpClient: AxiosInstance) => {
  const baseUrl = `/demand-types`;

  const getDemandTypes = async (): Promise<DemandTypeDto[]> => {
    const { data } = await httpClient.get<GetDemandTypesResponseDto>(baseUrl);
    return data.data;
  };

  const createDemandType = async (dto: CreateDemandTypeDto): Promise<DemandTypeDto> => {
    const { data } = await httpClient.post<CreateDemandTypeResponseDto>(baseUrl, dto);
    return data.data;
  };

  const updateDemandType = async (id: number, dto: UpdateDemandTypeDto): Promise<DemandTypeDto> => {
    const { data } = await httpClient.put<UpdateDemandTypeResponseDto>(`${baseUrl}/${id}`, dto);
    return data.data;
  };

  const deleteDemandType = async (id: number): Promise<void> => {
    await httpClient.delete(`${baseUrl}/${id}`);
  };

  const { t } = useTranslation('translation');
  const setSnackbarMessage = useSetSnackbarMessage();
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: demandTypesQueryKeys.all() });

  const useGetAll = () =>
    useQuery({
      queryKey: demandTypesQueryKeys.lists(),
      queryFn: getDemandTypes,
    });

  const useCreate = () =>
    useErrorHandlingMutation({
      mutationFn: createDemandType,
      onSuccess: (newType) => {
        queryClient.setQueryData<DemandTypeDto[]>(demandTypesQueryKeys.lists(), (old) =>
          old ? [...old, newType].sort((a, b) => a.name.localeCompare(b.name)) : [newType],
        );
        void invalidate();
      },
      onError: () =>
        setSnackbarMessage({
          message: t(($) => $.ideaManagement.general.demands.typeCreateError),
          severity: 'error',
        }),
    });

  const useUpdate = () =>
    useErrorHandlingMutation({
      mutationFn: ({ id, dto }: { id: number; dto: UpdateDemandTypeDto }) =>
        updateDemandType(id, dto),
      onSuccess: invalidate,
      onError: () =>
        setSnackbarMessage({
          message: t(($) => $.ideaManagement.general.demands.typeUpdateError),
          severity: 'error',
        }),
    });

  const useDelete = () =>
    useErrorHandlingMutation({
      mutationFn: deleteDemandType,
      onSuccess: invalidate,
    });

  return {
    getDemandTypes,
    invalidate,
    useGetAll,
    useCreate,
    useUpdate,
    useDelete,
  };
};
